/**
 * Recursive JSON tree viewer.
 *
 * Displays nested objects and arrays with collapsible nodes, inline copy buttons,
 * and special rendering for URLs and large values.
 */

import React, { useState } from 'react';
import { useTheme, fontSize, fontFamily, spacing, Icon } from '@fti/design-system';
import type { JsonTreeProps } from './contracts';
import { isHttpUrl, formatNumber, copyToClipboard } from './format';

interface TreeNodeProps {
  value: unknown;
  name?: string;
  depth: number;
  defaultExpandDepth: number;
}

function TreeNode({ value, name, depth, defaultExpandDepth }: TreeNodeProps) {
  const { theme } = useTheme();
  const [expanded, setExpanded] = useState(depth < defaultExpandDepth);
  const [showFull, setShowFull] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const indentPx = depth * 14;

  const keyStyle: React.CSSProperties = {
    color: theme.colors.text.secondary,
    fontFamily: fontFamily.mono,
    fontSize: fontSize.xs,
  };

  const valueStyle: React.CSSProperties = {
    color: theme.colors.text.primary,
    fontFamily: fontFamily.mono,
    fontSize: fontSize.xs,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  };

  const guideStyle: React.CSSProperties = {
    position: 'absolute',
    left: indentPx,
    top: 0,
    bottom: 0,
    width: '1px',
    background: theme.colors.neutral[200],
  };

  const handleCopy = async () => {
    const success = await copyToClipboard(value);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    }
  };

  // Null
  if (value === null) {
    return (
      <div style={{ position: 'relative', paddingLeft: indentPx + 'px', display: 'flex', alignItems: 'center', gap: spacing.xs }}>
        {depth > 0 && <div style={guideStyle} />}
        {name && <span style={keyStyle}>{name}:</span>}
        <span style={{ ...valueStyle, color: theme.colors.text.secondary }}>null</span>
        <CopyButton onClick={handleCopy} copied={copied} theme={theme} />
      </div>
    );
  }

  // Undefined
  if (value === undefined) {
    return (
      <div style={{ position: 'relative', paddingLeft: indentPx + 'px', display: 'flex', alignItems: 'center', gap: spacing.xs }}>
        {depth > 0 && <div style={guideStyle} />}
        {name && <span style={keyStyle}>{name}:</span>}
        <span style={{ ...valueStyle, color: theme.colors.text.secondary }}>undefined</span>
      </div>
    );
  }

  // Boolean
  if (typeof value === 'boolean') {
    return (
      <div style={{ position: 'relative', paddingLeft: indentPx + 'px', display: 'flex', alignItems: 'center', gap: spacing.xs }}>
        {depth > 0 && <div style={guideStyle} />}
        {name && <span style={keyStyle}>{name}:</span>}
        <span style={valueStyle}>{String(value)}</span>
        <CopyButton onClick={handleCopy} copied={copied} theme={theme} />
      </div>
    );
  }

  // Number
  if (typeof value === 'number') {
    return (
      <div style={{ position: 'relative', paddingLeft: indentPx + 'px', display: 'flex', alignItems: 'center', gap: spacing.xs }}>
        {depth > 0 && <div style={guideStyle} />}
        {name && <span style={keyStyle}>{name}:</span>}
        <span style={valueStyle}>{formatNumber(value)}</span>
        <CopyButton onClick={handleCopy} copied={copied} theme={theme} />
      </div>
    );
  }

  // String
  if (typeof value === 'string') {
    const isUrl = isHttpUrl(value);
    const isLong = value.length > 2000;
    const displayValue = isLong && !showFull ? value.slice(0, 2000) : value;

    return (
      <div style={{ position: 'relative', paddingLeft: indentPx + 'px', display: 'flex', alignItems: 'flex-start', gap: spacing.xs }}>
        {depth > 0 && <div style={guideStyle} />}
        {name && <span style={keyStyle}>{name}:</span>}
        {isUrl ? (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...valueStyle, color: theme.colors.primary[500], textDecoration: 'underline' }}
          >
            {displayValue}
          </a>
        ) : (
          <span style={valueStyle}>{displayValue}</span>
        )}
        {isLong && !showFull && (
          <button
            type="button"
            onClick={() => setShowFull(true)}
            style={{
              background: 'none',
              border: 'none',
              color: theme.colors.primary[500],
              fontSize: fontSize.xs,
              cursor: 'pointer',
              padding: 0,
              textDecoration: 'underline',
            }}
          >
            Show all ({value.length} chars)
          </button>
        )}
        <CopyButton onClick={handleCopy} copied={copied} theme={theme} />
      </div>
    );
  }

  // Array
  if (Array.isArray(value)) {
    const itemCount = value.length;
    const visibleCount = showMore ? itemCount : Math.min(100, itemCount);
    const hasMore = itemCount > visibleCount;

    return (
      <div style={{ position: 'relative' }}>
        <div style={{ paddingLeft: indentPx + 'px', display: 'flex', alignItems: 'center', gap: spacing.xs }}>
          {depth > 0 && <div style={guideStyle} />}
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              fontSize: fontSize.xs,
              color: theme.colors.text.secondary,
            }}
          >
            {expanded ? '▾' : '▸'}
          </button>
          {name && <span style={keyStyle}>{name}</span>}
          <span style={{ ...keyStyle, color: theme.colors.text.secondary }}>
            [{itemCount}]
          </span>
          <CopyButton onClick={handleCopy} copied={copied} theme={theme} />
        </div>
        {expanded && (
          <div>
            {value.slice(0, visibleCount).map((item, i) => (
              <TreeNode key={i} value={item} name={String(i)} depth={depth + 1} defaultExpandDepth={defaultExpandDepth} />
            ))}
            {hasMore && (
              <div style={{ paddingLeft: (depth + 1) * 14 + 'px', marginTop: spacing.xs }}>
                <button
                  type="button"
                  onClick={() => setShowMore(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: theme.colors.primary[500],
                    fontSize: fontSize.xs,
                    cursor: 'pointer',
                    padding: 0,
                    textDecoration: 'underline',
                  }}
                >
                  Show {itemCount - visibleCount} more
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Object
  if (typeof value === 'object') {
    const keys = Object.keys(value as Record<string, unknown>);
    const keyCount = keys.length;

    return (
      <div style={{ position: 'relative' }}>
        <div style={{ paddingLeft: indentPx + 'px', display: 'flex', alignItems: 'center', gap: spacing.xs }}>
          {depth > 0 && <div style={guideStyle} />}
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              fontSize: fontSize.xs,
              color: theme.colors.text.secondary,
            }}
          >
            {expanded ? '▾' : '▸'}
          </button>
          {name && <span style={keyStyle}>{name}</span>}
          <span style={{ ...keyStyle, color: theme.colors.text.secondary }}>
            {`{${keyCount} ${keyCount === 1 ? 'key' : 'keys'}}`}
          </span>
          <CopyButton onClick={handleCopy} copied={copied} theme={theme} />
        </div>
        {expanded && (
          <div>
            {keys.map((key) => (
              <TreeNode
                key={key}
                value={(value as Record<string, unknown>)[key]}
                name={key}
                depth={depth + 1}
                defaultExpandDepth={defaultExpandDepth}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Fallback for other types
  return (
    <div style={{ position: 'relative', paddingLeft: indentPx + 'px', display: 'flex', alignItems: 'center', gap: spacing.xs }}>
      {depth > 0 && <div style={guideStyle} />}
      {name && <span style={keyStyle}>{name}:</span>}
      <span style={valueStyle}>{String(value)}</span>
    </div>
  );
}

interface CopyButtonProps {
  onClick: () => void;
  copied: boolean;
  theme: ReturnType<typeof useTheme>['theme'];
}

function CopyButton({ onClick, copied, theme }: CopyButtonProps) {
  const [hover, setHover] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: 'none',
        border: 'none',
        padding: '2px',
        cursor: 'pointer',
        opacity: hover ? 1 : 0,
        transition: 'opacity 0.15s',
        display: 'flex',
        alignItems: 'center',
        color: copied ? theme.colors.semantic.success.default : theme.colors.text.secondary,
      }}
      aria-label="Copy to clipboard"
    >
      <Icon name={copied ? 'check' : 'copy'} size={12} />
    </button>
  );
}

export function JsonTree({ value, name, defaultExpandDepth = 1 }: JsonTreeProps) {
  return (
    <div style={{ fontFamily: 'monospace', lineHeight: 1.6 }}>
      <TreeNode value={value} name={name} depth={0} defaultExpandDepth={defaultExpandDepth} />
    </div>
  );
}
